// src/app/components/prompt-tracking/prompt-ai-section.tsx

import { useState, useEffect } from 'react';
import { Sparkles, ChevronDown, Lightbulb, RefreshCw, Loader2 } from 'lucide-react';
import { InsightActionButton } from '../insight-action-button';
import { UnderlineTabs, UnderlineTabsPanel } from '../ui/underline-tabs';
import { useBriefingInsights } from '../../hooks/prompt-tracking/useBriefingInsights';
import type { BriefingInsight } from '../../types/prompt-tracking-types';
import { AI_QUESTIONS_PLACEHOLDER } from '../../constants/prompt-tracking-constants';

interface PromptAISectionProps {
  /** Allow parent to pass navigate so "View Insights →" toast works */
  onNavigate?: (page: string) => void;
}

export function PromptAISection({ onNavigate }: PromptAISectionProps) {
  const [activeTab,       setActiveTab]       = useState<'questions' | 'briefing'>('questions');
  const [expandedInsight, setExpandedInsight] = useState<string | null>(null);

  const brandName =
    localStorage.getItem('bm_brand') ||
    sessionStorage.getItem('bm_brand') ||
    'default';

  const {
    insights,
    isLoading: insightsLoading,
    isRefreshing,
    hasFetched,
    fetchInsights,
    refreshInsights,
  } = useBriefingInsights();

  useEffect(() => {
    if (activeTab === 'briefing' && !hasFetched) {
      fetchInsights();
    }
  }, [activeTab, hasFetched, fetchInsights]);

  return (
    <div
      className="px-4 md:px-6 py-5 md:py-6 border rounded-xl mb-6 md:mb-8"
      style={{
        borderColor: 'var(--border)',
        // Match page bg so the UnderlineTabs row (which uses --background
        // internally) doesn't render as a white strip inside a grey card.
        backgroundColor: 'var(--background)',
      }}
    >
      {/* Section header */}
      <div className="flex items-center gap-3 mb-5 md:mb-6">
        <div
          className="w-10 h-10 rounded-lg flex items-center justify-center flex-shrink-0"
          style={{ backgroundColor: 'var(--spectral-violet-10)' }}
        >
          <Sparkles className="w-5 h-5" style={{ color: 'var(--spectral-violet)' }} />
        </div>
        <div className="min-w-0">
          <h3
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--foreground)',
              marginBottom: '2px',
            }}
          >
            AI Prompt Intelligence
          </h3>
          <p style={{ color: 'var(--muted-foreground)', fontSize: 'var(--text-sm)' }}>
            Your intelligent prompt optimization companion
          </p>
        </div>
      </div>

      <UnderlineTabs
        tabs={[
          { value: 'questions', label: 'Ask Questions' },
          { value: 'briefing',  label: 'Weekly Briefing' },
        ]}
        value={activeTab}
        onChange={(v) => setActiveTab(v as 'questions' | 'briefing')}
        size="sm"
        padding="px-0"
      >
        <UnderlineTabsPanel value="questions" className="pt-5 md:pt-6">
          <QuestionsTab />
        </UnderlineTabsPanel>

        <UnderlineTabsPanel value="briefing" className="pt-5 md:pt-6">
          <BriefingTab
            insights={insights}
            isLoading={insightsLoading}
            isRefreshing={isRefreshing}
            expandedInsight={expandedInsight}
            brandName={brandName}
            onToggleInsight={(id) => setExpandedInsight((prev) => (prev === id ? null : id))}
            onRefresh={refreshInsights}
            onNavigate={onNavigate}
          />
        </UnderlineTabsPanel>
      </UnderlineTabs>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Ask Questions tab
// ─────────────────────────────────────────────────────────────────────────────

function QuestionsTab() {
  return (
    <div>
      <h4
        style={{
          fontSize: 'var(--text-lg)',
          fontWeight: 'var(--font-weight-semibold)',
          marginBottom: '8px',
        }}
      >
        Ask me anything about your prompt performance
      </h4>
      <p
        style={{
          color: 'var(--muted-foreground)',
          fontSize: 'var(--text-sm)',
          lineHeight: '1.5',
          marginBottom: '16px',
        }}
      >
        Get AI-powered insights on sentiment trends, Share of Voice, and optimization opportunities.
      </p>
      <div className="relative">
        <textarea
          placeholder={AI_QUESTIONS_PLACEHOLDER}
          rows={3}
          className="w-full p-3 md:p-4 pr-14 rounded-xl resize-none"
          style={{
            backgroundColor: 'var(--muted-30)',
            border: '2px solid var(--border)',
            fontSize: 'var(--text-base)',
            lineHeight: '1.6',
            outline: 'none',
          }}
          onFocus={(e) => (e.target.style.borderColor = 'var(--spectral-violet)')}
          onBlur={(e)  => (e.target.style.borderColor = 'var(--border)')}
        />
        <button
          className="absolute bottom-3 right-3 w-10 h-10 rounded-lg flex items-center justify-center"
          style={{ backgroundColor: 'var(--spectral-violet)', border: 'none', cursor: 'pointer' }}
        >
          <Sparkles className="w-5 h-5" style={{ color: 'white' }} />
        </button>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Briefing tab
// ─────────────────────────────────────────────────────────────────────────────

interface BriefingTabProps {
  insights:        BriefingInsight[];
  isLoading:       boolean;
  isRefreshing:    boolean;
  expandedInsight: string | null;
  brandName:       string;
  onToggleInsight: (id: string) => void;
  onRefresh:       () => void;
  onNavigate?:     (page: string) => void;
}

function BriefingTab({
  insights, isLoading, isRefreshing, expandedInsight,
  brandName, onToggleInsight, onRefresh, onNavigate,
}: BriefingTabProps) {
  return (
    <div>
      {/* Header row — stacks on mobile so refresh button doesn't crush title */}
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
        <div className="min-w-0">
          <h4
            style={{
              fontSize: 'var(--text-lg)',
              fontWeight: 'var(--font-weight-semibold)',
              marginBottom: '4px',
            }}
          >
            Here's what's happening with your prompts
          </h4>
          <p
            style={{
              color: 'var(--muted-foreground)',
              fontSize: 'var(--text-sm)',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            Generated from your saved analysis runs and brand monitoring data.
          </p>
        </div>
        <button
          onClick={onRefresh}
          disabled={isRefreshing}
          className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg transition-all w-full sm:w-auto flex-shrink-0"
          style={{
            backgroundColor: 'var(--spectral-violet-10)',
            border: '1px solid var(--spectral-violet)',
            color: 'var(--spectral-violet)',
            fontSize: 'var(--text-sm)',
            fontWeight: 'var(--font-weight-medium)',
            cursor: isRefreshing ? 'default' : 'pointer',
            opacity: isRefreshing ? 0.7 : 1,
          }}
        >
          {isRefreshing
            ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
            : <><RefreshCw className="w-3.5 h-3.5" /> Refresh Insights</>}
        </button>
      </div>

      {/* Loading skeleton */}
      {isLoading && (
        <div className="space-y-2">
          {[1, 2, 3].map((i) => (
            <div
              key={i}
              className="h-12 rounded-lg animate-pulse"
              style={{ backgroundColor: 'var(--muted)' }}
            />
          ))}
        </div>
      )}

      {/* No insights yet */}
      {!isLoading && insights.length === 0 && (
        <div
          className="p-5 md:p-6 rounded-xl text-center"
          style={{ backgroundColor: 'var(--muted)', border: '1px solid var(--border)' }}
        >
          <Sparkles
            className="w-8 h-8 mx-auto mb-3"
            style={{ color: 'var(--muted-foreground)' }}
          />
          <p
            style={{
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-semibold)',
              color: 'var(--foreground)',
              marginBottom: '4px',
            }}
          >
            No briefing insights yet
          </p>
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
              marginBottom: '16px',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            Run analysis on your prompts first, then click Refresh Insights to generate your weekly briefing.
          </p>
          <button
            onClick={onRefresh}
            disabled={isRefreshing}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg"
            style={{
              backgroundColor: 'var(--spectral-violet)',
              color: 'white',
              border: 'none',
              fontSize: 'var(--text-sm)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: isRefreshing ? 'default' : 'pointer',
            }}
          >
            {isRefreshing
              ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Generating…</>
              : <><Sparkles className="w-3.5 h-3.5" /> Generate Briefing</>}
          </button>
        </div>
      )}

      {/* Insight rows */}
      {!isLoading && insights.length > 0 && (
        <div className="space-y-2">
          {insights.map((insight) => (
            <BriefingInsightRow
              key={insight.id}
              insight={insight}
              brandName={brandName}
              isExpanded={expandedInsight === insight.id}
              onToggle={() => onToggleInsight(insight.id)}
              onNavigate={onNavigate}
            />
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────
// Single briefing row
// ─────────────────────────────────────────────────────────────────────────────

function BriefingInsightRow({
  insight, brandName, isExpanded, onToggle, onNavigate,
}: {
  insight:     BriefingInsight;
  brandName:   string;
  isExpanded:  boolean;
  onToggle:    () => void;
  onNavigate?: (page: string) => void;
}) {
  return (
    <div
      className="rounded-lg overflow-hidden transition-all"
      style={{
        backgroundColor: 'var(--muted-30)',
        borderTopWidth: '1px', borderTopStyle: 'solid', borderTopColor: isExpanded ? 'var(--border)' : 'transparent',
        borderRightWidth: '1px', borderRightStyle: 'solid', borderRightColor: isExpanded ? 'var(--border)' : 'transparent',
        borderBottomWidth: '1px', borderBottomStyle: 'solid', borderBottomColor: isExpanded ? 'var(--border)' : 'transparent',
        borderLeftWidth: '3px', borderLeftStyle: 'solid', borderLeftColor: insight.iconColor,
      }}
    >
      <div
        onClick={onToggle}
        className="flex items-start gap-2 px-3 py-2 cursor-pointer hover:bg-background/50 rounded-lg transition-all"
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                color: insight.iconColor,
              }}
            >
              {insight.metric}
            </span>
            <span
              style={{
                fontSize: 'var(--text-base)',
                fontWeight: 'var(--font-weight-semibold)',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {insight.title}
            </span>
          </div>
          {!isExpanded && (
            <p
              style={{
                fontSize: 'var(--text-sm)',
                color: 'var(--muted-foreground)',
                lineHeight: '1.4',
                marginTop: '4px',
                overflowWrap: 'anywhere',
                wordBreak: 'break-word',
              }}
            >
              {insight.subtitle}
            </p>
          )}
        </div>
        <ChevronDown
          className={`w-4 h-4 shrink-0 transition-transform mt-0.5 ${isExpanded ? 'rotate-180' : ''}`}
          style={{ color: 'var(--muted-foreground)' }}
        />
      </div>

      {isExpanded && (
        <div className="px-3 pb-3 space-y-3">
          <p
            style={{
              fontSize: 'var(--text-xs)',
              color: 'var(--muted-foreground)',
              lineHeight: '1.5',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {insight.subtitle}
          </p>
          <p
            style={{
              fontSize: 'var(--text-sm)',
              lineHeight: '1.5',
              overflowWrap: 'anywhere',
              wordBreak: 'break-word',
            }}
          >
            {insight.details}
          </p>

          {/* AI Recommendation — stacks on mobile so save button wraps below */}
          <div
            className="p-3 rounded-lg"
            style={{ backgroundColor: 'var(--background)', border: '1px solid var(--border)' }}
          >
            <div className="flex flex-col sm:flex-row sm:items-center gap-3">
              <Lightbulb
                className="w-4 h-4 flex-shrink-0"
                style={{ color: 'var(--spectral-violet)' }}
              />
              <div className="flex-1 min-w-0">
                <div
                  style={{
                    fontSize: 'var(--text-xs)',
                    fontWeight: 'var(--font-weight-semibold)',
                    color: 'var(--foreground)',
                    marginBottom: '4px',
                  }}
                >
                  AI Recommendation
                </div>
                <p
                  style={{
                    fontSize: 'var(--text-sm)',
                    color: 'var(--muted-foreground)',
                    lineHeight: '1.5',
                    overflowWrap: 'anywhere',
                    wordBreak: 'break-word',
                  }}
                >
                  {insight.recommendation}
                </p>
              </div>

              <div className="flex-shrink-0 self-start sm:self-auto">
                <InsightActionButton
                  title={insight.title}
                  content={[insight.subtitle, insight.details, insight.recommendation]}
                  context={{ module: 'prompt-tracking', stageName: 'Prompt Briefing', timestamp: new Date() }}
                  brandName={brandName}
                  onNavigateToInsights={() => onNavigate?.('report-generator')}
                  size="sm"
                />
              </div>
            </div>
          </div>

          <div className="flex items-center gap-2 mt-3 flex-wrap">
            <span style={{ fontSize: 'var(--text-xs)', color: 'var(--muted-foreground)' }}>
              Impact:
            </span>
            <span
              className="px-2 py-0.5 rounded-md"
              style={{
                fontSize: 'var(--text-xs)',
                fontWeight: 'var(--font-weight-semibold)',
                backgroundColor: insight.impact === 'High' ? 'var(--infrared-coral-10)' : 'var(--signal-blue-10)',
                color:           insight.impact === 'High' ? 'var(--infrared-coral)'    : 'var(--signal-blue)',
              }}
            >
              {insight.impact}
            </span>
          </div>
        </div>
      )}
    </div>
  );
}